import { Maybe } from '@warehouse/graphql';
import { TitleSearchDisplayNameFullHTML } from '@warehouse/title/core';

interface TitleSearchDisplayName {
	title: string;
	subTitle1?: string;
	subTitle2?: string;
}

export default function titleSearchDisplayNameBuilder({
	tokens,
	titleSort = false,
}: {
	tokens?: TitleSearchDisplayNameFullHTML;
	titleSort?: boolean;
}): TitleSearchDisplayName {
	if (!tokens) {
		return { title: '' };
	}
	const title = titleSort ? tokens.titleSortFrontEnd || tokens.titleFrontEnd! : tokens.titleFrontEnd!;

	return buildBasedOnWorkType({
		title,
		tokens,
		workType: tokens.workType,
	});
}

const buildBasedOnWorkType = ({
	title,
	tokens,
	workType,
}: {
	title: string;
	tokens: TitleSearchDisplayNameFullHTML;
	workType?: string;
}): { title: string; subTitle1: string; subTitle2: string } => {
	switch (workType) {
		case 'Season': {
			return seasonTitleSearchDisplayNameBuilder({ title, tokens });
		}
		case 'Episode': {
			return episodeTitleSearchDisplayNameBuilder({ title, tokens });
		}
		case 'Compilation':
		case 'Supplemental': {
			return prefixWithWorkTypeDetailsTitleSearchDisplayNameBuilder({ title, tokens });
		}
		case 'Edit': {
			const titles = buildBasedOnWorkType({
				title,
				workType: tokens.parentWorkType === 'Edit' ? tokens.parentAbstractionWorkType : tokens.parentWorkType,
				tokens,
			});

			return {
				...titles,
				subTitle1: mergeValues(
					columnSeparator,
					tokens.editUse,
					tokens.editClasses?.join(', '),
					tokens.madeForRegions?.join(', '),
					tokens.madeForRegionsExclude ? `Excl. ${tokens.madeForRegionsExclude.join(', ')}` : undefined,
					tokens.versionLanguages?.join(', '),
					tokens.editDescription,
				),
			};
		}
		case 'Manifestation': {
			const titles = buildBasedOnWorkType({
				title,
				workType: tokens.parentWorkType === 'Manifestation' ? tokens.parentAbstractionWorkType : tokens.parentWorkType,
				tokens,
			});

			return {
				...titles,
				subTitle2: tokens.identification ?? '',
			};
		}
		default:
			return {
				title: prefixWithParentTitleSearchDisplayName(tokens, title, columnSeparator),
				subTitle1: '',
				subTitle2: '',
			};
	}
};

const seasonTitleSearchDisplayNameBuilder = ({
	title,
	tokens,
}: {
	title: string;
	tokens: TitleSearchDisplayNameFullHTML;
}) => {
	const seasonPresentInName = stringContainsSeason(title);
	const titlePre = `Season ${tokens.sequenceNumber}${seasonPresentInName ? '' : ` - ${title}`}`;

	return {
		title: prefixWithParentTitleSearchDisplayName(tokens, titlePre, columnSeparator),
		subTitle1: mergeValues(
			columnSeparator,
			tokens.madeForRegions?.join(', '),
			tokens.madeForRegionsExclude ? `Excl. ${tokens.madeForRegionsExclude.join(', ')}` : undefined,
		),
		subTitle2: '',
	};
};

const episodeTitleSearchDisplayNameBuilder = ({
	title,
	tokens,
}: {
	title: string;
	tokens: TitleSearchDisplayNameFullHTML;
}) => {
	const episodePresentInName = stringContainsEpisode(title);
	const titlePre = `Ep ${tokens.sequenceNumber}${episodePresentInName ? '' : ` - ${title}`}`;
	return {
		title: prefixWithParentTitleSearchDisplayName(tokens, titlePre, columnSeparator),
		subTitle1: '',
		subTitle2: '',
	};
};

const prefixWithWorkTypeDetailsTitleSearchDisplayNameBuilder = ({
	title,
	tokens,
}: {
	title: string;
	tokens: TitleSearchDisplayNameFullHTML;
}) => {
	if (tokens.workTypeDetails?.length) {
		return {
			title: mergeValues(columnSeparator, tokens.workTypeDetails[0], title),
			subTitle1: '',
			subTitle2: '',
		};
	}
	return {
		title,
		subTitle1: '',
		subTitle2: '',
	};
};

const columnSeparator = ': ';

const stringContainsEpisode = (str?: Maybe<string>) => !!str?.match(/\b(?:E|EP|EPS|EPISODE)\s*#?:?\s*(\d+)/gim);

const stringContainsSeason = (str?: Maybe<string>) => !!str?.match(/\b(?:SEASON|S)\s*#?:?\s*(\d+)/gim);

function prefixWithParentTitleSearchDisplayName(
	tokens: TitleSearchDisplayNameFullHTML,
	suffix: Maybe<string> | undefined,
	separator: string,
): string {
	if (!suffix) return tokens.parentTitleSearchDisplayNameFullForChild || '';

	if (
		(tokens.parentTitleSearchDisplayNameFullForChild === undefined ||
			tokens.parentTitleSearchDisplayNameFullForChild === null) &&
		(suffix === undefined || suffix === null)
	) {
		return '';
	}
	if (tokens.parentTitleSearchDisplayNameFullForChild) {
		return `${tokens.parentTitleSearchDisplayNameFullForChild}${separator}${suffix}`;
	}
	return suffix ?? '';
}

function mergeValues(separator: string, ...values: (undefined | Maybe<string>)[]): string {
	return values.filter((token): token is string => token !== undefined && token !== null).join(separator);
}
